import { useCallback, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

const TILE_DETAIL_PAGE_SIZE = 50;

// same accumulate-as-you-scroll pattern as the dashboard's Activity Logs panel,
// generalized so every "click a tile, see its records" drawer can share one
// implementation instead of hand-rolling pagination state per tile
export function useDashboardTileDetail(queryDoc, resultField) {
  const [fetch, { loading }] = useLazyQuery(queryDoc);

  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const pageRef = useRef(1);
  const variablesRef = useRef({});

  // merges straight off the settled query result instead of watching the
  // hook's `data` via useEffect - Apollo's cache returns the same `data`
  // reference when a fresh response normalizes to something deeply equal to
  // what's already cached (e.g. reopening the same tile with the same ids),
  // which left a useEffect([data]) never re-firing and rows stuck at the
  // empty array `open()` had just cleared them to
  const fetchPage = useCallback(async (page, variables) => {
    pageRef.current = page;
    const result = await fetch({
      fetchPolicy: 'network-only',
      variables: { page, pageSize: TILE_DETAIL_PAGE_SIZE, ...variables },
    });

    const response = result?.data?.[resultField]?.response;
    if (!response) return;
    // a newer open()/loadMore() call already moved the page on - drop this stale response
    if (pageRef.current !== page) return;

    const total = Number(result?.data?.[resultField]?.totalCount || 0);
    setRows(prev => {
      const merged = page === 1 ? response : [...prev, ...response];
      setHasMore(merged.length < total);
      return merged;
    });
    setTotalCount(total);
  }, [fetch, resultField]);

  // called when a tile is clicked - starts a fresh page-1 load for that tile's variables (e.g. its ids)
  const open = useCallback((variables) => {
    variablesRef.current = variables || {};
    setRows([]);
    setHasMore(true);
    fetchPage(1, variablesRef.current);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading) return;
    fetchPage(pageRef.current + 1, variablesRef.current);
  }, [loading, fetchPage]);

  // lets a row be patched in place after an action (e.g. Fulfill) without a full refetch
  const patchRow = useCallback((id, patch) => {
    setRows(prev => prev.map(row => (row?._id === id ? { ...row, ...patch } : row)));
  }, []);

  return { rows, loading, hasMore, totalCount, open, loadMore, patchRow };
}
